import DIBRenderSpace from "./render-spaces/dib-render-space";
import TimingInfo from "./timing-info";
import AnimateProxyBase from "./animate-proxys/animate-proxy-base";
import { ServiceBase } from "./services/service-base";
import messengerHub from "./SMS/defaultMessengerHub";
import MessengerHub from "./SMS/MessengerHub";
import { Lp5PlayerAdapter } from "./lp5/lp5-player-adapter";
import { LessonDataPage } from "./lp5/lesson-data-format-page";

declare global {
  interface Window {
    DIB_BASE_SINGLETON: DropinSupervisorBase;
    DIB_RESERVED_InitClass: any;
    DIB_WIDGET_CONFIG: any;
  }
}

/**
 * @category Dropin Supervisor Base
 * @description The Dropin Supervisor Baseclass was meant to replace the flash player as much as possible, which is effectively a virtual machine.
 * It is responsible for managing the render loop, and the various services that are available to the dropin.
 * It also manages the render spaces, which manage specific rendering libraries/contexts (such as createjs, threejs, etc.)
 *
 * The Dropin Supervisor Baseclass is a singleton, and is available to the Dropin as a global variable called window.DIB_BASE_SINGLETON.
 *
 *
 *
 */
export class DropinSupervisorBase {
  protected renderSpaceList: DIBRenderSpace[] = [];
  protected proxys: AnimateProxyBase[] = [];

  // todo:  generate bindings for captivate
  /**
   * @deprecated
   * @group Captivate
   */
  public _captivate: any;

  protected Lp5Player: Lp5PlayerAdapter;

  protected frameZeroCalled: boolean = false;
  protected _timingInfo: TimingInfo;
  protected frameZeroLambda: () => void;
  protected requestAnimFrameLambda: (timingInfo?: TimingInfo) => void;
  protected _services: { [name: string]: ServiceBase } = {};
  protected onGoto;

  protected _messengerHub: MessengerHub;

  protected loadingCount = 0;
  protected loadingQueue: Promise<void>[] = [];
  protected frameZeroAttempted: boolean = false;
  protected _smsCommandChannel = "all-dropins-command-channel";

  protected commandListeners: { [command: string]: any[] } = {};

  protected pageData: LessonDataPage = null;

  public get timingInfo(): TimingInfo {
    return this._timingInfo;
  }

  //  PROGRESS STUFF
  protected _lastStep: number = 0;
  protected _currentStep: number = 0;

  /** @group Pagination */
  public get currentStep(): number {
    return this._currentStep;
  }
  /** @group Pagination */
  public set currentStep(step: number) {
    this._currentStep = step;
  }

  /** @group Pagination */
  public get lastStep(): number {
    return this._lastStep;
  }
  public set lastStep(step: number) {
    this._lastStep = step;
  }

  // =============================
  //      captivate getters and setters
  // =============================
  /** @group Captivate */
  public get captivate(): any {
    return this._captivate;
  }
  public set captivate(captivate: any) {
    this._captivate = captivate;
  }

  /** @group Captivate */
  public get captivateAPI(): any {
    // if its not a widget then don't try to dig into that object
    if (!this.isWidget()) return null;

    // get the api interface
    return this.captivate.CPMovieHandle.getCPAPIInterface();
  }

  /** @group Captivate */
  public get captivateEventEmitter(): any {
    // if its not a widget then don't try to dig into that object
    if (!this.isWidget()) return null;
    return this.captivate.CPMovieHandle.getCPAPIEventEmitter();
  }

  // =============================
  //      LP5 PLAYER interface
  // =============================
  public setLP5Player() {
    this.Lp5Player = new Lp5PlayerAdapter(this);
  }

  // need to bring the sms into typescript
  public getSMS(): MessengerHub {
    return this._messengerHub;
  }

  // this needs to be renamed to lessonDataPage
  public setLessonPageData(pageData: LessonDataPage) {
    this.pageData = pageData;
  }

  // ========================================
  //          SCORING/SUCCESS
  // ========================================
  /** @group Scoring */
  setSuccess() {
    this.setSuccessState(true);
  }

  /** @group Scoring */
  setFailure() {
    this.setSuccessState(false);
  }

  /** @group Scoring */
  setSuccessState(successState) {
    if (this.Lp5Player) {
      this.Lp5Player.setSuccess(successState);
    }
    if (!this.isWidget()) return;
    var lInteractiveWidgetHandle =
      this.captivate.CPMovieHandle.getInteractiveWidgetHandle();
    lInteractiveWidgetHandle.setSuccess(successState);
  }

  /** @group Scoring */
  setScore(scoreValue) {
    if (this.Lp5Player) {
      this.Lp5Player.setScore(scoreValue);
    }
  }

  // =============================
  //      PROGRESSION
  // =============================
  /** @group Pagination */
  protected _onGotoStep: (
    requestedStep: number,
    currentStep?: number,
    command?: string | object
  ) => any;

  /** @group Pagination */
  public onChangeStep(
    eventHandler: (currentStep: number, nextStep?: number, command?: any) => any
  ) {
    this._onGotoStep = eventHandler;
  }
  /** @group Pagination */
  gotoStep(step: number, command = null): any {
    if (this._onGotoStep) {
      const results = this._onGotoStep(step, this.currentStep, command);

      return results;
    }

    // if we make it here they haven't set a results thing
    if (this.currentStep > this.lastStep) this.currentStep = step;
  }

  public getStatusObject(commandOutput = null) {
    return {
      commandOutput,
      currentStep: this.currentStep,
      lastStep: this.lastStep,
    };
  }

  /**
   * @group Pagination
   * @description
   * this method currently doesn't do anything unless extendded from a baseclass
   */
  public setCurrentStep(step: number) {}

  // not literally captivate, but mite as well be, so its going here

  // =================================
  //          SINGLETON PATTERN
  // =================================
  private static _singleton: DropinSupervisorBase;
  public static get singleton(): DropinSupervisorBase {
    return this._singleton;
  }

  private registerSingleton(singleton: DropinSupervisorBase) {
    if (!window.DIB_BASE_SINGLETON) {
      this.bindToWindow("DIB_BASE_SINGLETON", this);
      DropinSupervisorBase._singleton = this;
    } else {
      throw new Error(
        "There should only be one supervisor instance per dropin"
      );
    }
  }

  // =================================
  //          ACTUAL CONSTRUCTOR
  // =================================
  constructor() {
    // set up the messenger hub
    this._messengerHub = messengerHub;

    // bind the init class to the window for widgets
    this.bindToWindow("DIB_RESERVED_InitClass", () => {
      return this;
    });

    // bootstrap the lp5 player adapter
    this.Lp5Player = new Lp5PlayerAdapter(this);

    // register the singleton
    this.registerSingleton(this);

    window.addEventListener("resize", this.onWindowResize.bind(this));

    // register load promises
    // this is a hook for blocking the onframezero call
    this.registerLoadPromises();

    // call the hook for initializing services
    this.initServices();
  }

  // =================================
  //          SERVICES

  // =================================
  /**
   * @group Services
   *  @description Services are a way to create additional,
   *  swappable modules
   */
  public registerService(service: ServiceBase) {
    const name = service.name;
    if (!this._services[name]) {
      this._services[name] = service;

      service.setSupervisor(this);
      service.registerLoadPromises();
    }
  }

  /**
   * @group Services
   * @description this is a hook specifically for registering services
   * service can block onframezero, so they can't be initiated
   * inside of it!
   */
  public initServices() {}

  /** @group Services */
  public getService<ServiceType extends ServiceBase>(
    name: string
  ): ServiceType {
    if (this._services[name] && this._services[name]) {
      return <ServiceType>this._services[name];
    }
    return null;
  }

  // =================================
  // LOADING PROMISES
  // =================================
  /** @group Lifecycle */
  public addLoadPromise(loadPromise: Promise<void>) {
    this.loadingCount++;
    this.loadingQueue.push(loadPromise);
    loadPromise.then(() => {
      this.loadingCount--;
      if (!this.loadingCount && this.frameZeroAttempted) {
        this.frameZero();
      }
    });
  }

  /**
   * @group Lifecycle
   * @description   this is a general place for load promises
   *  by default we will include the threejs fonts
   *
   */

  protected registerLoadPromises() {
    // add the font loading promise
  }

  public registerProxyForUpdates(proxy: AnimateProxyBase) {
    if (this.proxys.indexOf(proxy) == -1) {
      this.proxys.push(proxy);
    }
  }

  // add a render space to the management list
  /** @group Render Spaces */
  public addRenderSpace(newSpace: DIBRenderSpace): void {
    //
    if (this.renderSpaceList.indexOf(newSpace) == -1) {
      this.renderSpaceList.push(newSpace);

      // add this supervisor to the renderspace
      newSpace.setSupervisor(this);

      // if we're already in the framezero phase, then go ahead
      // and initialize this ones frame zero
      if (this.frameZero) {
        newSpace.onFrameZero();
      }
    }
  }

  // provide a way to get the renderspace by type
  // this is only good for singletons
  /** @group Render Spaces */
  getRenderSpace<RenderspaceType extends DIBRenderSpace>(
    filterType: new (...args: any[]) => RenderspaceType
  ): RenderspaceType {
    let retVal = null;
    this.renderSpaceList.forEach((renderSpace) => {
      if (renderSpace instanceof filterType) {
        retVal = renderSpace;
      }
    });
    return retVal;
  }

  // when the window resizes, notify each of the
  /** @group Render Spaces */
  protected onWindowResize(): void {
    this.renderSpaceList.forEach((renderSpace: DIBRenderSpace) => {
      renderSpace.onWindowResize();
    });
  }

  // =============================
  //      ON FRAME ZERO HANDLING
  // =============================

  // basically a stub for extension
  /**
   * @group Lifecycle
   */
  protected onFrameZero() {}

  /**
   * @group Lifecycle
   */
  public externalFrameZero(doOnFrameZero: () => void) {
    this.frameZeroLambda = doOnFrameZero;
  }

  // ====================================================
  /**
   * @group Lifecycle
   * @description
   * frame zero is where we do all of our startup
   * it can only be called once.  For libraries like Animate or Away, it is important to be able to trigger
   * the framezero call from the library itself, so this serves as a way to do that.
   */
  // ====================================================
  frameZero() {
    // mark that we attempted this for the loading process
    this.frameZeroAttempted = true;
    if (this.loadingCount) return;

    // make sure its only called once
    if (this.frameZeroCalled) return;
    this.frameZeroCalled = true;

    // ===================================================
    //      use our event system to navigate stuff
    // ===================================================
    this.addCommandListener("NEXT", () => {
      const commandOutput = this.gotoStep(this.currentStep + 1);
      return commandOutput;
    });

    this.addCommandListener("PREVIOUS", () => {
      const commandOutput = this.gotoStep(this.currentStep - 1);
      return commandOutput;
    });

    this.addCommandListener("REPLAY", () => {
      const commandOutput = this.gotoStep(0);
      return commandOutput;
    });

    messengerHub.subscribe(this._smsCommandChannel, (message) => {
      // figure out what command we should be doing
      let command = null;
      if (typeof message == "string") {
        command = message.toUpperCase();
      } else if (message.command) {
        command = message.command.toUpperCase();
      }

      // trigger any command listeners that have been added
      if (
        this.commandListeners[command] &&
        this.commandListeners[command].length
      ) {
        let retVal = null;
        this.commandListeners[command].forEach((listener, key) => {
          retVal = this.getStatusObject(listener(message));
        });
        return retVal;
      }
    });

    // onframezero any of the render spaces that have already been added (perhaps
    // through a main imports)
    this.renderSpaceList.forEach((renderSpace: DIBRenderSpace) => {
      renderSpace.onFrameZero();
    });

    // call onframezero for everything
    this.onFrameZero();
    if (this.frameZeroLambda) {
      this.frameZeroLambda();
    }
    this.Lp5Player.onFrameZero();

    // ===================================================
    //      DO STUFF WITH THE CONFIG
    // ===================================================
    if (window.DIB_WIDGET_CONFIG) {
      const { width = null, height = null } = window.DIB_WIDGET_CONFIG;

      // see if we can resize the frame element
      if (window.frameElement && width && height) {
        const el = frameElement as HTMLIFrameElement;
        el.style.width = `${width}px`;
        el.style.height = `${height}px`;

        document.body.style.padding = "0";
        document.body.style.margin = "0";

        // simulate a resize
        window.setTimeout(() => this.onWindowResize(), 0);
      }
    }

    var animate = () => {
      this.proxys.forEach((proxy: AnimateProxyBase) => {
        proxy.onRequestAnimFrame(this.timingInfo);
      });

      //
      this.renderSpaceList.forEach((renderSpace: DIBRenderSpace) => {
        renderSpace.onRequestAnimFrame(this.timingInfo);
      });

      // call request anim frame for the supervisor for everything
      this.onRequestAnimFrame(this.timingInfo);

      // use the external one as well so that extension is not required
      if (this.requestAnimFrameLambda) {
        this.requestAnimFrameLambda(this.timingInfo);
      }

      requestAnimationFrame(animate); // Call itself whenever an animation frame event happens

      this.timingInfo.frameComplete();
    };

    //

    this._timingInfo = new TimingInfo(new Date());
    animate(); // Call this the first time to get the process started.
  }

  // command listeners allow you to add an event listener for commands from
  // captivate buttons
  protected addCommandListener(
    commandName: string,
    listener: (message) => any
  ): number {
    commandName = commandName.toUpperCase();
    if (!this.commandListeners[commandName]) {
      this.commandListeners[commandName] = [];
    }

    return this.commandListeners[commandName].push(listener);
  }

  // =============================
  //      ON ANIM FRAME HANDLING
  // =============================

  // basically a stub for extension
  protected onRequestAnimFrame(timingInfo?: TimingInfo) {}

  /** @group Lifecycle */
  public externalRequestAnimFrame(
    doOnRequestAnimFrame: (timingInfo?: TimingInfo) => void
  ) {
    this.requestAnimFrameLambda = doOnRequestAnimFrame;
  }

  // this utility is mostly for the sake of making things play nice
  // with typescript, rather than defining window interfaces everywhere for things
  // that will never be referenced by any of these scripts

  bindToWindow(variableName: string, object: any): void {
    window[variableName] = object;
  }

  /** @group Captivate */
  isWidget(): boolean {
    return !!this.captivate || this.Lp5Player.isWidget();
  }

  // onload is called exclusively for widgets
  /** @group Lifecycle */
  onLoad() {
    // There's a whole lot of stuff available here. You'll probably want to console log this.captivate some time.

    // only gets called in widgets
    try {
      window["DIB_RESERVED_init"]();
    } catch (e) {
      console.log(e);
    }
  }

  public static CurrentVersion = "2.1.2";
}

export default DropinSupervisorBase;
